/** Package Detail — Monthly cost/price breakdown, useful when rate cards change
 *  mid-package. Also exposes an Invoice preview link per month. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FileText, Wallet, Loader2, RefreshCw, Globe, Building2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const fmtMoney = (v, cur = "INR") => v == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(v).toLocaleString("en-IN")}`;

export default function PackageMonthlyBreakdown({ packageId, currency }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = () => {
    setBusy(true);
    api.get(`/packages/${packageId}/monthly-breakdown`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiErrorMessage(e, "Load failed")))
      .finally(() => setBusy(false));
  };
  useEffect(() => { if (packageId) load(); /* eslint-disable-next-line */ }, [packageId]);

  const openInvoice = async (month) => {
    try {
      const { data: d } = await api.get(`/packages/${packageId}/invoice-preview?month=${month}`);
      setPreview(d);
    } catch (e) { toast.error(apiErrorMessage(e, "Preview failed")); }
  };

  if (!data && busy) return <div className="p-6 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 mr-2 inline animate-spin" />Loading breakdown…</div>;
  if (!data) return null;

  const flags = data.flags || {};
  const showPrice = flags.can_view_rates !== false;
  const showCost = flags.can_view_costs !== false;

  return (
    <div className="space-y-4" data-testid="pkg-monthly-breakdown">
      <div className="flex items-center gap-2">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Monthly breakdown · {data.count} months</div>
          <div className="text-xs text-muted-foreground">
            {data.rate_card_changes > 0
              ? <span className="text-amber-600">⚠ Rate card changes {data.rate_card_changes} time{data.rate_card_changes === 1 ? "" : "s"} across this package's lifespan.</span>
              : "Rate card is stable across this package's lifespan."}
          </div>
        </div>
        <Button size="sm" variant="ghost" className="rounded-none h-7 ml-auto" onClick={load} disabled={busy} data-testid="pkg-mb-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {data.months.length > 1 && (showPrice || showCost) && (
        <div className="border p-3" data-testid="pkg-mb-chart">
          <div className="h-56">
            <ResponsiveContainer>
              <LineChart data={data.months} margin={{ top: 8, right: 12, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "var(--background)", border: "1px solid var(--border)", fontSize: 11 }} formatter={(v) => fmtMoney(v, currency)} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {showPrice && <Line type="monotone" dataKey="monthly_price" stroke="#10b981" strokeWidth={2} dot={false} name="Price" />}
                {showCost && <Line type="monotone" dataKey="monthly_cost" stroke="#f59e0b" strokeWidth={2} dot={false} name="Cost" />}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="border overflow-x-auto" data-testid="pkg-mb-table">
        <table className="w-full text-xs min-w-[720px]">
          <thead className="bg-muted/40 text-left text-[10px] uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-1.5">Month</th>
              <th className="px-3 py-1.5">Rate card</th>
              <th className="px-3 py-1.5 text-right">Hours</th>
              {showPrice && <th className="px-3 py-1.5 text-right">Price</th>}
              {showCost && <th className="px-3 py-1.5 text-right">Cost</th>}
              {(flags.can_view_margins !== false) && <th className="px-3 py-1.5 text-right">Margin %</th>}
              <th className="px-3 py-1.5 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {data.months.map((m, idx) => {
              const prev = data.months[idx - 1];
              const cardChanged = prev && prev.rate_card?.id !== m.rate_card?.id;
              return (
                <tr key={m.month} className={`border-t ${cardChanged ? "bg-amber-500/5" : ""}`} data-testid={`pkg-mb-row-${m.month}`}>
                  <td className="px-3 py-1.5 font-mono">{m.month}{cardChanged && <Badge variant="outline" className="rounded-none text-[9px] ml-2 text-amber-600 border-amber-500/40">rate change</Badge>}</td>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex items-center gap-1">
                      {m.rate_card?.kind === "brand_override"
                        ? <Building2 className="w-3 h-3 text-violet-500" />
                        : <Globe className="w-3 h-3 text-sky-500" />}
                      <span className="truncate max-w-40">{m.rate_card?.name}</span>
                      {m.rate_card?.version && <span className="text-[10px] text-muted-foreground">v{m.rate_card.version}</span>}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-right font-mono">{m.monthly_hours}</td>
                  {showPrice && <td className="px-3 py-1.5 text-right font-mono text-emerald-700">{fmtMoney(m.monthly_price, currency)}</td>}
                  {showCost && <td className="px-3 py-1.5 text-right font-mono text-amber-700">{fmtMoney(m.monthly_cost, currency)}</td>}
                  {(flags.can_view_margins !== false) && <td className={`px-3 py-1.5 text-right font-mono ${m.margin_pct < 0 ? "text-red-600" : "text-emerald-700"}`}>{m.margin_pct}%</td>}
                  <td className="px-3 py-1.5 text-right">
                    {showPrice && (
                      <button className="text-[10px] inline-flex items-center gap-1 hover:text-primary" onClick={() => openInvoice(m.month)} data-testid={`pkg-mb-invoice-${m.month}`}>
                        <FileText className="w-3 h-3" />Invoice
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {preview && <InvoicePreviewDialog preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

function InvoicePreviewDialog({ preview, onClose }) {
  const cur = preview.currency || "INR";
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="invoice-preview-dialog">
        <DialogHeader>
          <DialogTitle>Invoice preview · {preview.month}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div className="border p-3">
              <div className="text-[10px] uppercase text-muted-foreground">Bill to</div>
              <div className="font-semibold">{preview.brand?.name || "—"}</div>
              {preview.brand?.billing_email && <div className="text-xs text-muted-foreground">{preview.brand.billing_email}</div>}
              {preview.brand?.gstin && <div className="text-xs text-muted-foreground">GSTIN: {preview.brand.gstin}</div>}
            </div>
            <div className="border p-3">
              <div className="text-[10px] uppercase text-muted-foreground">Rate card used</div>
              <div className="inline-flex items-center gap-1 font-semibold">
                {preview.rate_card?.kind === "brand_override"
                  ? <Building2 className="w-3 h-3 text-violet-500" />
                  : <Globe className="w-3 h-3 text-sky-500" />}
                {preview.rate_card?.name}
                <Badge variant="outline" className="rounded-none text-[9px]">v{preview.rate_card?.version}</Badge>
              </div>
              <div className="text-xs text-muted-foreground">{preview.rate_card?.source}</div>
            </div>
          </div>
          <div className="border" data-testid="invoice-preview-lines">
            <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-muted/40 text-[10px] uppercase text-muted-foreground">
              <div className="col-span-5">Line</div>
              <div className="col-span-3">Category</div>
              <div className="col-span-1 text-right">Qty</div>
              <div className="col-span-1 text-right">Rate</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>
            {preview.lines.map((ln) => (
              <div key={ln.id} className="grid grid-cols-12 gap-2 px-3 py-2 items-center border-t text-xs">
                <div className="col-span-5">{ln.name}</div>
                <div className="col-span-3"><Badge variant="outline" className="rounded-none text-[10px]">{ln.category}</Badge></div>
                <div className="col-span-1 text-right font-mono">{ln.monthly_qty}</div>
                <div className="col-span-1 text-right font-mono">{fmtMoney(ln.rate, cur)}</div>
                <div className="col-span-2 text-right font-mono">{fmtMoney(ln.amount, cur)}</div>
              </div>
            ))}
          </div>
          <div className="flex justify-end">
            <div className="border p-3 w-72 text-xs space-y-1">
              <div className="flex justify-between"><span>Subtotal</span><span className="font-mono">{fmtMoney(preview.totals.subtotal, cur)}</span></div>
              {preview.totals.discount_pct > 0 && <div className="flex justify-between text-muted-foreground"><span>Discount ({preview.totals.discount_pct}%)</span><span className="font-mono">−{fmtMoney(preview.totals.subtotal - preview.totals.net, cur)}</span></div>}
              <div className="flex justify-between font-bold text-sm border-t pt-1"><span>Net payable</span><span className="font-mono">{fmtMoney(preview.totals.net, cur)}</span></div>
            </div>
          </div>
          <div className="border border-dashed p-3 text-[11px] text-muted-foreground flex items-start gap-2">
            <Wallet className="w-3.5 h-3.5 mt-0.5 shrink-0" />
            This is a preview using the effective rate card for {preview.month}. A dedicated Invoicing module (sequence numbering, GST, PDF, dispatch, tracking) is next on the roadmap.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
